import type { TutorBilling, TutorBillingResponse, UpdateTutorBillingPayload } from '../../model/legal/interfaces'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTutorLegalStore } from '../../model/legal/store'

interface UpdateTutorBillingApiOptions {
  tutorId: string | number
  payload: UpdateTutorBillingPayload
  files?: File[]
  onStart?: () => void
  onSuccess?: (billing: TutorBilling) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

/** Laravel reads `$request->file('document.files')`, and multipart bodies are only
 *  parsed on POST — so we spoof PATCH via `_method` and bracket-encode the payload. */
const buildFormData = (payload: UpdateTutorBillingPayload, files: File[]): FormData => {
  const formData = new FormData()
  formData.append('_method', 'PATCH')

  Object.entries(payload.personal).forEach(([key, value]) => formData.append(`personal[${key}]`, value))
  Object.entries(payload.document).forEach(([key, value]) => formData.append(`document[${key}]`, value))
  Object.entries(payload.payment).forEach(([key, value]) => formData.append(`payment[${key}]`, value))

  files.forEach(file => formData.append('document[files][]', file))

  return formData
}

/** Guard against stale completions: a save finished after navigating to another
 *  tutor must not overwrite that tutor's store state or fire callbacks/toasts. */
const isRouteStillCurrent = (tutorId: string | number): boolean => {
  const currentId = useRouter().currentRoute.value.params.id
  return String(currentId) === String(tutorId)
}

/** Monotonic token: only the latest request may clear the shared loading flags. */
let requestSeq = 0

export const useUpdateTutorBillingApi = async (options: UpdateTutorBillingApiOptions): Promise<TutorBilling | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.tutors.billing', { tutor_id: options.tutorId })

  const tutorLegalStore = useTutorLegalStore()
  const commonStore = useCommonStore()

  const seq = ++requestSeq

  options.onStart?.()
  commonStore.setLoading(true)
  tutorLegalStore.setLoading(true)

  try {
    const formData = buildFormData(options.payload, options.files ?? [])
    const { data } = await $axios.post<{ data: TutorBillingResponse }>(url!, formData)
    const billing: TutorBilling | null = data.data?.billing || null

    if (!isRouteStillCurrent(options.tutorId)) return billing

    // A 200 without a billing body means the contract is broken — treat it as
    // an error instead of wiping existingFiles from the store under a success toast.
    if (!billing) {
      toast.error('Unexpected response — billing info may not be saved')
      return null
    }

    tutorLegalStore.setBilling(billing)
    toast.success('Billing info updated successfully')

    options.onSuccess?.(billing)

    return billing
  } catch (err: unknown) {
    console.error(err)
    if (isRouteStillCurrent(options.tutorId)) toast.error(getAxiosErrorMessage(err))
    options.onError?.(err)

    return null
  } finally {
    if (seq === requestSeq) {
      commonStore.setLoading(false)
      tutorLegalStore.setLoading(false)
    }
    options.onFinish?.()
  }
}
